import React, { useState } from 'react';
import './ContactPage.css';
import { db } from '../../firebaseConfig';
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";

const conversationTypes = [
    'Architecture / Technology Advisory',
    'Speaking / Workshop',
    'Book / Course / Content Collaboration',
    'Resource Request',
    'Education Technology Discussion',
    'Software Delivery / Engineering Partnership',
    'Mentoring / Career Guidance',
    'Other'
];

const preferredContactMethods = [
    'Email',
    'Phone / WhatsApp',
    'Calendar Meeting'
];

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        organizationRole: '',
        country: '',
        phone: '',
        conversationType: '',
        preferredContactMethod: '',
        message: ''
    });
    const contactsRef = collection(db, "contacts");
    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: '' }); // Clear errors on change
    };

    const validatePhoneNumber = (number) => {
        const digitsOnly = number.replace(/\D/g, '');
        if (digitsOnly.length < 7 || digitsOnly.length > 15) {
            return 'Please enter a valid phone number.';
        }

        if (/^(\d)\1+$/.test(digitsOnly)) {
            return 'Phone number cannot contain repeated digits only.';
        }

        // Check for sequential ascending or descending digits (basic check)
        if ('1234567890'.includes(digitsOnly) || '0987654321'.includes(digitsOnly)) {
            return 'Phone number cannot be sequential.';
        }

        return ''; // Return empty string if no errors
    };

    const validate = () => {
        let tempErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isPhoneContactPreferred = formData.preferredContactMethod === 'Phone / WhatsApp';

        if (!formData.name.trim()) {
            tempErrors.name = 'Please enter your name.';
        }

        if (!emailRegex.test(formData.email)) {
            tempErrors.email = 'Please enter a valid email address.';
        }

        if (!formData.organizationRole.trim()) {
            tempErrors.organizationRole = 'Please enter your organization or role.';
        }

        if (!formData.country.trim()) {
            tempErrors.country = 'Please enter your country.';
        }

        if (isPhoneContactPreferred && !formData.phone.trim()) {
            tempErrors.phone = 'Please enter a phone number for Phone / WhatsApp contact.';
        } else if (formData.phone) {
            const phoneError = validatePhoneNumber(formData.phone);
            if (phoneError) {
                tempErrors.phone = phoneError;
            }
        }

        if (!formData.conversationType) {
            tempErrors.conversationType = 'Please select a conversation type.';
        }

        if (!formData.preferredContactMethod) {
            tempErrors.preferredContactMethod = 'Please select a preferred contact method.';
        }

        if (!formData.message.trim()) {
            tempErrors.message = 'Please share a few details.';
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return; // Stop the submission if there are validation errors

        setIsSubmitting(true);
        setSubmitSuccess(false);

        try {
            // TODO: Add Firebase App Check / reCAPTCHA v3 before enabling higher-volume submissions.
            // A Firebase Cloud Function or Firebase Trigger Email extension can
            // listen to new contact documents here and send notification email later.
            await setDoc(doc(contactsRef), {
                name: formData.name,
                email: formData.email,
                organizationRole: formData.organizationRole,
                country: formData.country,
                phone: formData.phone,
                conversationType: formData.conversationType,
                preferredContactMethod: formData.preferredContactMethod,
                message: formData.message,
                status: 'new',
                source: 'ashutosh.net.in contact form',
                createdAt: serverTimestamp(),
            });

            setSubmitSuccess(true);
            setFormData({
                name: '',
                email: '',
                organizationRole: '',
                country: '',
                phone: '',
                conversationType: '',
                preferredContactMethod: '',
                message: ''
            });
        } catch {
            setErrors({ submit: 'Something went wrong. Please email me directly.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" className="contact-section">
            <div className="contact-inner">
                <div className="contact-copy">
                    <div className="section-kicker">Contact</div>
                    <h2>Start a Conversation</h2>
                    <p>
                        Have an idea, collaboration request, speaking opportunity,
                        resource request, or technology discussion in mind? Share a few
                        details and I will review it.
                    </p>
                    <div className="contact-direct-links">
                        <a href="mailto:hello@ashutosh.net.in">hello@ashutosh.net.in</a>
                        <a
                            href="https://cal.com/ashutoshshashi/professional-conversation"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Schedule a professional conversation
                        </a>
                    </div>
                </div>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="contact-form-row">
                        <div className="form-group">
                            <label htmlFor="name">Name</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your name"
                                required
                            />
                            {errors.name && <div className="error-message">{errors.name}</div>}
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="name@example.com"
                                required
                            />
                            {errors.email && <div className="error-message">{errors.email}</div>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="organizationRole">Organization / role</label>
                        <input
                            type="text"
                            id="organizationRole"
                            name="organizationRole"
                            value={formData.organizationRole}
                            onChange={handleChange}
                            placeholder="Company, institution, role, or independent"
                            required
                        />
                        {errors.organizationRole && <div className="error-message">{errors.organizationRole}</div>}
                    </div>

                    <div className="contact-form-row">
                        <div className="form-group">
                            <label htmlFor="country">Country</label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="Country"
                                required
                            />
                            {errors.country && <div className="error-message">{errors.country}</div>}
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone number <span className="optional-label">conditional</span></label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Optional"
                            />
                            <p className="helper-text">Required only if you prefer Phone / WhatsApp contact.</p>
                            {errors.phone && <div className="error-message">{errors.phone}</div>}
                        </div>
                    </div>

                    <div className="contact-form-row">
                        <div className="form-group">
                            <label htmlFor="conversationType">Conversation type</label>
                            <select
                                id="conversationType"
                                name="conversationType"
                                value={formData.conversationType}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a topic</option>
                                {conversationTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                            {errors.conversationType && <div className="error-message">{errors.conversationType}</div>}
                        </div>
                        <div className="form-group">
                            <label htmlFor="preferredContactMethod">Preferred contact method</label>
                            <select
                                id="preferredContactMethod"
                                name="preferredContactMethod"
                                value={formData.preferredContactMethod}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a method</option>
                                {preferredContactMethods.map((method) => (
                                    <option key={method} value={method}>
                                        {method}
                                    </option>
                                ))}
                            </select>
                            {errors.preferredContactMethod && <div className="error-message">{errors.preferredContactMethod}</div>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Share context, goals, timeline, or useful links."
                            required
                        />
                        {errors.message && <div className="error-message">{errors.message}</div>}
                    </div>
                    <button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : submitSuccess ? 'Submitted' : 'Send message'}
                    </button>
                    {submitSuccess && (
                        <p className="success-message">
                            Thanks for reaching out. I will get back to you soon.
                        </p>
                    )}
                    {errors.submit && <p className="error-message form-error">{errors.submit}</p>}
                </form>
            </div>
        </section>
    );
};

export default ContactPage;
